import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { Link, useLocation } from "react-router-dom";
import LanguageToggle from "./LanguageToggle";
import { useLang } from "../i18n/LanguageContext";
import { useFavorites } from "../context/FavoritesContext";
import logo from "../assets/logoLong.png";

const BP = 768; // mobile breakpoint

export default function Navbar() {
  const { t } = useLang();
  const { favorites } = useFavorites();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close the menu whenever the route changes.
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  // While open: lock body scroll, close on Escape, close if resized to desktop.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    const onResize = () => window.innerWidth > BP && setOpen(false);
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    window.addEventListener("resize", onResize);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("resize", onResize);
    };
  }, [open]);

  const links = [
    { to: "/how", label: t("nav.how") },
    { to: "/saved", label: t("nav.saved"), count: favorites.length },
    { to: "/about", label: t("footer.about") },
    { to: "/faq", label: t("footer.faq") },
    { to: "/contact", label: t("footer.contact") },
  ];

  return (
    <>
      <Nav $scrolled={scrolled}>
        <Brand
          to="/"
          aria-label="Amora Weddings"
          onClick={() => setOpen(false)}
        >
          <img src={logo} alt="Amora Weddings" />
        </Brand>

        {/* Desktop links */}
        <Right>
          <NavLink to="/how">{t("nav.how")}</NavLink>
          <NavLink to="/list">{t("nav.forBusiness")}</NavLink>
          <Saved to="/saved">
            ♥ {t("nav.saved")}
            {favorites.length > 0 && <em>{favorites.length}</em>}
          </Saved>
          <LanguageToggle />
        </Right>

        {/* Mobile hamburger */}
        <Burger
          type="button"
          onClick={() => setOpen((o) => !o)}
          aria-label={open ? "Close menu" : "Open menu"}
          aria-expanded={open}
          aria-controls="mobile-menu"
          $open={open}
        >
          <span />
          <span />
          <span />
        </Burger>
      </Nav>

      {/* Mobile dropdown */}
      <Overlay $open={open} onClick={() => setOpen(false)} aria-hidden="true" />
      <Menu id="mobile-menu" $open={open} role="dialog" aria-modal="true">
        <MenuLinks>
          {links.map((l) => (
            <MenuLink key={l.to} to={l.to} onClick={() => setOpen(false)}>
              <span>{l.label}</span>
              {l.count > 0 && <em>{l.count}</em>}
            </MenuLink>
          ))}
        </MenuLinks>
        <MenuFoot>
          <LanguageToggle />
          <Cta to="/list" onClick={() => setOpen(false)}>
            {t("nav.forBusiness")}
          </Cta>
        </MenuFoot>
      </Menu>
    </>
  );
}

const Nav = styled.nav`
  position: sticky;
  top: 0;
  z-index: 50;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px clamp(20px, 5vw, 64px);
  background: rgba(251, 247, 239, 0.82);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid
    ${({ $scrolled, theme }) => ($scrolled ? theme.colors.hair : "transparent")};
  transition: border-color 0.3s;
`;

const Brand = styled(Link)`
  display: flex;
  align-items: center;
  img {
    height: 60px;
    width: auto;
    display: block;
    @media (max-width: 768px) {
      height: 38px;
    }
    @media (max-width: 480px) {
      height: 30px;
    }
  }
`;

const Right = styled.div`
  display: flex;
  align-items: center;
  gap: 18px;
  @media (max-width: 768px) {
    display: none;
  }
`;

const NavLink = styled(Link)`
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.inkSoft};
  &:hover {
    color: ${({ theme }) => theme.colors.ink};
  }
`;

const Saved = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.rose};
  em {
    font-style: normal;
    font-size: 11px;
    background: ${({ theme }) => theme.colors.rose};
    color: #fff;
    padding: 1px 6px;
    border-radius: 100px;
  }
`;

const Burger = styled.button`
  display: none;
  position: relative;
  width: 42px;
  height: 42px;
  border: none;
  background: transparent;
  cursor: pointer;
  z-index: 51;
  padding: 0;
  @media (max-width: 768px) {
    display: inline-flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 5px;
  }
  span {
    display: block;
    width: 24px;
    height: 2px;
    border-radius: 2px;
    background: ${({ theme }) => theme.colors.ink};
    transition:
      transform 0.3s ease,
      opacity 0.2s ease;
  }
  span:nth-child(1) {
    transform: ${({ $open }) =>
      $open ? "translateY(7px) rotate(45deg)" : "none"};
  }
  span:nth-child(2) {
    opacity: ${({ $open }) => ($open ? 0 : 1)};
  }
  span:nth-child(3) {
    transform: ${({ $open }) =>
      $open ? "translateY(-7px) rotate(-45deg)" : "none"};
  }
`;

const Overlay = styled.div`
  display: none;
  @media (max-width: 768px) {
    display: block;
    position: fixed;
    inset: 0;
    z-index: 48;
    background: rgba(34, 40, 30, 0.42);
    opacity: ${({ $open }) => ($open ? 1 : 0)};
    visibility: ${({ $open }) => ($open ? "visible" : "hidden")};
    transition:
      opacity 0.3s ease,
      visibility 0.3s ease;
  }
`;

const Menu = styled.div`
  display: none;
  @media (max-width: 768px) {
    display: block;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    z-index: 49;
    background: ${({ theme }) => theme.colors.cream};
    border-bottom: 1px solid ${({ theme }) => theme.colors.hair};
    box-shadow: 0 24px 48px rgba(34, 40, 30, 0.16);
    padding: 88px clamp(20px, 6vw, 32px) 28px;
    max-height: 100dvh;
    overflow-y: auto;
    transform: translateY(${({ $open }) => ($open ? "0" : "-102%")});
    transition: transform 0.38s cubic-bezier(0.4, 0, 0.2, 1);
  }
`;

const MenuLinks = styled.nav`
  display: flex;
  flex-direction: column;
`;

const MenuLink = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 17px 2px;
  font-size: 19px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.ink};
  border-bottom: 1px solid ${({ theme }) => theme.colors.hair};
  &:active {
    color: ${({ theme }) => theme.colors.green};
  }
  em {
    font-style: normal;
    font-size: 12px;
    font-weight: 600;
    background: ${({ theme }) => theme.colors.rose};
    color: #fff;
    padding: 2px 9px;
    border-radius: 100px;
  }
`;

const MenuFoot = styled.div`
  margin-top: 22px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
`;

const Cta = styled(Link)`
  background: ${({ theme }) => theme.colors.green};
  color: ${({ theme }) => theme.colors.cream};
  font-size: 15px;
  font-weight: 500;
  padding: 13px 26px;
  border-radius: 100px;
`;
